import type {
  Movimentacao,
  MovimentacaoItemIntake,
  MovimentacaoItemExecucao,
  TipoEquipamentoMovimentacao,
  SistemaOperacionalMovimentacao,
  PropriedadeEquipamento,
} from './movimentacao.types';
import type { StatusEtapaProcesso } from './solicitacao.types';

/** Visão unificada de um item de equipamento — junta intake + execução por `id`, só pra
 * exibição/edição nas telas. Nunca é o formato gravado no Firestore (ver Movimentacao). */
export interface MovimentacaoEquipamentoView {
  id: string;
  tipoEquipamento: TipoEquipamentoMovimentacao | null;
  sistemaOperacional?: SistemaOperacionalMovimentacao | null;
  homologado?: boolean | null;
  propriedadeEquipamento?: PropriedadeEquipamento | null;
  equipamentoId?: string | null;
  modelo?: string | null;
  patrimonio?: string | null;
  nSerie?: string | null;
  senha?: string | null;
  condicao?: string | null;
  acessorios?: string[] | null;
  acessoriosOutros?: string | null;
  observacao?: string | null;
  descricaoLivre?: string | null;
  glpiAtualizado?: boolean | null;
  fotos?: string[] | null;
}

export function mesclarEquipamentosMovimentacao(
  intake: MovimentacaoItemIntake[] = [],
  execucao: MovimentacaoItemExecucao[] = []
): MovimentacaoEquipamentoView[] {
  const execucaoPorId = new Map(execucao.map((item) => [item.id, item]));

  return intake.map((item) => {
    const execucaoDoItem = execucaoPorId.get(item.id);
    return {
      ...item,
      equipamentoId: execucaoDoItem?.equipamentoId ?? null,
      modelo: execucaoDoItem?.modelo ?? null,
      patrimonio: execucaoDoItem?.patrimonio ?? null,
      nSerie: execucaoDoItem?.nSerie ?? null,
      senha: execucaoDoItem?.senha ?? null,
      condicao: execucaoDoItem?.condicao ?? null,
      acessorios: execucaoDoItem?.acessorios ?? null,
      acessoriosOutros: execucaoDoItem?.acessoriosOutros ?? null,
      observacao: execucaoDoItem?.observacao ?? null,
      glpiAtualizado: execucaoDoItem?.glpiAtualizado ?? null,
      fotos: execucaoDoItem?.fotos ?? null,
    };
  });
}

/** Mínimo de fotos por equipamento pra etapa "Fotos" da Movimentação ficar concluída. */
export const FOTOS_MINIMAS_POR_EQUIPAMENTO = 2;

/**
 * "Itens simples" da Movimentação — cartão de acesso e chip: patrimoniados no Inventário, mas
 * sem modelo nem nº de série obrigatório, e 1 foto basta (decisão do SI). Mesma lista em
 * functions/src/si/concluirSolicitacao.ts (TIPOS_ITEM_SIMPLES).
 */
export const TIPOS_ITEM_SIMPLES = ['cartao_acesso', 'chip'];

export function ehItemSimples(tipoEquipamento: string | null | undefined): boolean {
  return Boolean(tipoEquipamento && TIPOS_ITEM_SIMPLES.includes(tipoEquipamento));
}

export function fotosMinimasDoItem(tipoEquipamento: string | null | undefined): number {
  return ehItemSimples(tipoEquipamento) ? 1 : FOTOS_MINIMAS_POR_EQUIPAMENTO;
}

/**
 * Deriva `etapas.fotos` — concluída quando todo equipamento da Movimentação tem pelo menos
 * fotosMinimasDoItem fotos próprias (2; cartão de acesso, 1). Movimentação com fotos legadas (nível
 * Movimentação, de antes das fotos por equipamento) conta como concluída, pra não gerar
 * pendência retroativa. Recalculada pelo movimentacaoService a cada escrita que mexe em
 * equipamentos/fotos — o valor gravado é o que concluirSolicitacao (Cloud Function) confere.
 */
export function calcularEtapaFotos(
  movimentacao: Pick<Movimentacao, 'fotos' | 'equipamentosIntake' | 'equipamentosExecucao'>
): StatusEtapaProcesso {
  if ((movimentacao.fotos ?? []).length > 0) return 'concluido';

  const itens = mesclarEquipamentosMovimentacao(movimentacao.equipamentosIntake, movimentacao.equipamentosExecucao);
  if (itens.length === 0) return 'pendente';

  const todosComFotos = itens.every((item) => (item.fotos ?? []).length >= fotosMinimasDoItem(item.tipoEquipamento));
  return todosComFotos ? 'concluido' : 'pendente';
}
